import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  customMemoryStatCode,
  customTasteStatCode,
  getActiveQuestionnaire,
  submitExperience,
} from '../services/experience-service'
import type {
  AnswerSubmission,
  ExperienceSubmission,
  Question,
  Questionnaire,
} from '../types/questionnaire'

function createSubmissionId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `submission-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const useExperienceStore = defineStore('experience', () => {
  const questionnaire = ref<Questionnaire | null>(null)
  const currentStepIndex = ref(0)
  const answers = ref<Record<string, string>>({})
  const customAnswers = ref<Record<string, string>>({})
  const loading = ref(false)
  const submitting = ref(false)
  const errorMessage = ref('')
  const lastSubmittedTasteCode = ref('')
  const lastSubmittedMemoryCode = ref('')
  const lastSubmissionQueued = ref(false)

  const currentStep = computed(() => questionnaire.value?.steps[currentStepIndex.value] ?? null)
  const isLastStep = computed(() => {
    if (!questionnaire.value) {
      return false
    }

    return currentStepIndex.value === questionnaire.value.steps.length - 1
  })

  async function ensureQuestionnaire() {
    if (questionnaire.value) {
      return
    }

    loading.value = true
    errorMessage.value = ''

    try {
      questionnaire.value = await getActiveQuestionnaire()
    } catch {
      errorMessage.value = '内容加载失败，请稍后重试'
    } finally {
      loading.value = false
    }
  }

  async function startSession() {
    resetSession()
    await ensureQuestionnaire()
  }

  function setAnswer(questionCode: string, value: string) {
    answers.value[questionCode] = value
    errorMessage.value = ''
  }

  function setCustomAnswer(questionCode: string, value: string) {
    customAnswers.value[questionCode] = value
    errorMessage.value = ''
  }

  function isQuestionValid(question: Question) {
    if (!question.required) {
      return true
    }

    const answer = answers.value[question.code]?.trim()

    if (!answer) {
      return false
    }

    if (answer === 'custom') {
      return Boolean(customAnswers.value[question.code]?.trim())
    }

    return true
  }

  function validateCurrentStep() {
    const step = currentStep.value

    if (!step) {
      return false
    }

    const valid = step.questions.every(isQuestionValid)
    errorMessage.value = valid ? '' : '请完成当前页面的必填内容'
    return valid
  }

  function nextStep() {
    if (!validateCurrentStep() || !questionnaire.value || isLastStep.value) {
      return false
    }

    currentStepIndex.value += 1
    return true
  }

  function previousStep() {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value -= 1
    }
  }

  function buildAnswers(): AnswerSubmission[] {
    if (!questionnaire.value) {
      return []
    }

    const result: AnswerSubmission[] = []

    questionnaire.value.steps.forEach((step) => {
      step.questions.forEach((question) => {
        const value = answers.value[question.code] || ''

        if (question.type === 'short_text') {
          result.push({
            questionCode: question.code,
            textValue: value.trim(),
          })
          return
        }

        if (value === 'custom') {
          result.push({
            questionCode: question.code,
            optionCode: 'custom',
            textValue: customAnswers.value[question.code]?.trim(),
          })
          return
        }

        result.push({
          questionCode: question.code,
          optionCode: value,
        })
      })
    })

    return result
  }

  async function submitCurrentSession() {
    if (!questionnaire.value || !validateCurrentStep() || submitting.value) {
      return false
    }

    submitting.value = true
    errorMessage.value = ''

    const payload: ExperienceSubmission = {
      submissionId: createSubmissionId(),
      questionnaireId: questionnaire.value.id,
      questionnaireVersion: questionnaire.value.version,
      answers: buildAnswers(),
    }

    try {
      const result = await submitExperience(payload)
      lastSubmissionQueued.value = result.queued
      lastSubmittedTasteCode.value = answers.value.taste === 'custom'
        ? customTasteStatCode(customAnswers.value.taste || '')
        : answers.value.taste || ''
      lastSubmittedMemoryCode.value = answers.value.memory_category === 'custom'
        ? customMemoryStatCode(customAnswers.value.memory_category || '')
        : answers.value.memory_category || ''
      return true
    } catch {
      errorMessage.value = '提交失败，内容已保留，请重试'
      return false
    } finally {
      submitting.value = false
    }
  }

  function resetSession() {
    currentStepIndex.value = 0
    answers.value = {}
    customAnswers.value = {}
    submitting.value = false
    errorMessage.value = ''
    lastSubmittedTasteCode.value = ''
    lastSubmittedMemoryCode.value = ''
    lastSubmissionQueued.value = false
  }

  return {
    questionnaire,
    currentStepIndex,
    answers,
    customAnswers,
    loading,
    submitting,
    errorMessage,
    lastSubmittedTasteCode,
    lastSubmittedMemoryCode,
    lastSubmissionQueued,
    currentStep,
    isLastStep,
    ensureQuestionnaire,
    startSession,
    setAnswer,
    setCustomAnswer,
    validateCurrentStep,
    nextStep,
    previousStep,
    submitCurrentSession,
    resetSession,
  }
})
